import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { useState } from 'react';

export function Landing() {
  const { isSignedIn } = useAuth();
  const navigate = useNavigate();
  const [monthlySpend, setMonthlySpend] = useState(500);
  const [activeTab, setActiveTab] = useState<'oneclick' | 'byoo'>('oneclick');

  const actualCost = Math.round(monthlySpend * 0.07);
  const savings = monthlySpend - actualCost;
  const annualSavings = savings * 12;

  const handlePricingClick = () => {
    document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleGetStarted = () => {
    navigate(isSignedIn ? '/deploy' : '/sign-in');
  };

  return (
    <div>
      {/* NAV */}
      <nav className="landing-nav">
        <div className="nav-inner">
          <a href="#" className="landing-logo">CLAWZEMPIC</a>
          <div className="nav-right">
            <a href="#pricing" onClick={(e) => { e.preventDefault(); handlePricingClick(); }}>Pricing</a>
            <a href="https://docs.clawzempic.ai" target="_blank" rel="noopener noreferrer">Docs</a>
            <a href="#pricing" className="nav-cta" onClick={(e) => { e.preventDefault(); handleGetStarted(); }}>Start Your Transformation</a>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="hero">
        <div className="hero-content">
          <div className="hero-lobsters">
            <img src="/before-clawzempic.png" alt="Before - hungry lobster" />
            <span className="hero-arrow">&rarr;</span>
            <img src="/after-clawzempic.png" alt="After - healthy lobster" />
          </div>

          <h1>OpenClaw but <em className="sexy-grad">sexier</em><br />and <span className="gold-grad">93% less expensive</span></h1>

          <p className="hero-sub"><strong>OpenClaw costs trust fund money to run, is a security nightmare, and is a pain in the Brazilian butt lift to set up.</strong> Our expert team of aesthetic Lobster MDs will turn your molting lobster into a sexy pageant winner in minutes!</p>

          {/* Calculator inline in hero */}
          <div className="hero-calc">
            <div className="calc-card">
              <div className="calc-label">
                <span>Monthly Claude spend</span>
                <div className="calc-amount">${monthlySpend.toLocaleString()}</div>
              </div>
              <input
                type="range"
                className="calc-slider"
                min={50}
                max={5000}
                step={50}
                value={monthlySpend}
                onChange={(e) => setMonthlySpend(Number(e.target.value))}
                style={{ '--slider-value': monthlySpend } as React.CSSProperties}
              />
              <div className="calc-how">Intelligent model routing + prompt caching + context compression</div>

              <div className="calc-results">
                <div className="calc-result green-box">
                  <div className="calc-result-label">With Clawzempic</div>
                  <div className="calc-result-value g-grad">${actualCost.toLocaleString()}</div>
                  <div className="calc-result-sub">per month</div>
                </div>
                <div className="calc-result gold-box">
                  <div className="calc-result-label">You Save</div>
                  <div className="calc-result-value gold-grad">${savings.toLocaleString()}</div>
                  <div className="calc-result-sub">${annualSavings.toLocaleString()}/year</div>
                </div>
              </div>
            </div>
          </div>

          <div className="hero-cta-wrap">
            <button className="btn-big" onClick={handleGetStarted}>Start Your Transformation</button>
            <span className="micro-copy">5-minute setup. Cancel anytime.</span>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="how-section" id="how">
        <div className="container">
          <div className="section-header">
            <h2>Why teams <span className="g-grad">switch.</span></h2>
          </div>

          <div className="how-grid">
            <div className="how-step">
              <div className="how-step-number">Save Money</div>
              <div className="how-step-title">Cut your bill by 93%</div>
              <ul className="how-bullets">
                <li>Simple tasks auto-route to cheaper models</li>
                <li>Prompt caching eliminates redundant tokens</li>
                <li>Free scripts handle tasks without calling the LLM at all</li>
              </ul>
            </div>
            <div className="how-step">
              <div className="how-step-number">Safe &amp; Secure</div>
              <div className="how-step-title">Sleep at night</div>
              <ul className="how-bullets">
                <li>Your own private VPS in the cloud</li>
                <li>Built-in prompt injection protection</li>
                <li>Hardened OpenClaw config out of the box</li>
              </ul>
            </div>
            <div className="how-step">
              <div className="how-step-number">Painless Setup</div>
              <div className="how-step-title">Live in 5 minutes</div>
              <ul className="how-bullets">
                <li>We host and manage everything</li>
                <li>Save hours of time</li>
                <li>End your FOMO. <strong>Become a lobster FAST</strong></li>
              </ul>
            </div>
          </div>

          <div className="byoo-callout">Already have OpenClaw and want to join the Clawzempic revolution? We have just the prescription for you: <a href="#pricing" className="byoo-callout-link" onClick={(e) => { e.preventDefault(); handlePricingClick(); }}>our BYOOC plan (Bring Your Own OpenClaw)</a></div>
        </div>
      </section>

      {/* PRICING */}
      <section className="pricing-section" id="pricing">
        <div className="container">
          <div className="section-header">
            <h2>Choose your plan.</h2>
            <p>All plans include routing, caching, compression, scripts, and memory.</p>
          </div>

          <div className="toggle-wrap">
            <div className="toggle-pills">
              <button
                className={`toggle-pill ${activeTab === 'oneclick' ? 'active' : ''}`}
                onClick={() => setActiveTab('oneclick')}
              >
                1-Click OpenClaw
              </button>
              <button
                className={`toggle-pill ${activeTab === 'byoo' ? 'active' : ''}`}
                onClick={() => setActiveTab('byoo')}
              >
                Bring Your Own
              </button>
            </div>
          </div>
          <div className="toggle-sub">
            {activeTab === 'oneclick' ? 'We host everything. You just show up.' : 'Already running OpenClaw? One config change.'}
          </div>

          <div className={`price-panel ${activeTab === 'oneclick' ? 'active' : ''}`}>
            <div className="price-grid-3">
              <div className="price-card">
                <div className="price-tier t-blue">Starter</div>
                <h3>Starter</h3>
                <div className="price-num">$29<span>/mo</span></div>
                <p className="price-tagline">Side projects that deserve to survive.</p>
                <ul className="price-list">
                  <li>$25/mo inference included</li>
                  <li>10,000 requests/month</li>
                  <li>Smart routing + caching + scripts</li>
                  <li>Hosted OpenClaw on dedicated VPS</li>
                  <li>Dashboard + analytics</li>
                </ul>
                <button className="price-btn-ghost" onClick={handleGetStarted}>Get Started</button>
              </div>
              <div className="price-card pop">
                <div className="price-badge">Most Popular</div>
                <div className="price-tier t-green">Pro</div>
                <h3>Pro</h3>
                <div className="price-num">$99<span>/mo</span></div>
                <p className="price-tagline">Teams shipping real products.</p>
                <ul className="price-list">
                  <li>$100/mo inference included</li>
                  <li>100,000 requests/month</li>
                  <li>All routing + compression + scripts</li>
                  <li>Hosted OpenClaw on dedicated VPS</li>
                  <li>Priority support</li>
                </ul>
                <button className="price-btn" onClick={handleGetStarted}>Get Started</button>
              </div>
              <div className="price-card">
                <div className="price-tier t-gold">Scale</div>
                <h3>Scale</h3>
                <div className="price-num">$299<span>/mo</span></div>
                <p className="price-tagline">Agents that never sleep.</p>
                <ul className="price-list">
                  <li>$500/mo inference included</li>
                  <li>500,000 requests/month</li>
                  <li>Custom routing rules + scripts</li>
                  <li>Hosted OpenClaw on dedicated VPS</li>
                  <li>Slack support &lt;1hr</li>
                </ul>
                <button className="price-btn-ghost" onClick={handleGetStarted}>Get Started</button>
              </div>
            </div>
          </div>

          <div className={`price-panel ${activeTab === 'byoo' ? 'active' : ''}`}>
            <div className="price-grid-1">
              <div className="price-card byoo-card">
                <img src="/models1.png" alt="BYOO lobster" className="byoo-img" />
                <div className="price-tier t-coral">Bring Your Own OpenClaw</div>
                <h3>BYOO</h3>
                <div className="price-num">$49<span>/mo</span></div>
                <p className="price-tagline">Already running OpenClaw? Just point it at us.</p>
                <ul className="price-list" style={{ textAlign: 'left' }}>
                  <li>Flat fee — bring your own API key</li>
                  <li>One config change, instant setup</li>
                  <li>Same routing + caching + compression</li>
                  <li>Scripts library included</li>
                  <li>Usage dashboard</li>
                </ul>
                <button className="price-btn" onClick={handleGetStarted}>Get Started</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER CTA */}
      <section className="footer-cta">
        <div className="container">
          <div className="footer-cta-inner">
            <img src="/after-clawzempic.png" alt="Healthy lobster" />
            <h2>Ready for the <span className="g-grad">ultimate glow-up?</span></h2>
            <button className="btn-big" onClick={handleGetStarted}>Start Your Transformation</button>
            <p className="footer-quip">No crash diets. No fad APIs. Just science.</p>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="marketing-footer">
        <div className="footer-inner">
          <div className="footer-grid">
            <div className="footer-brand">
              <div className="footer-logo">CLAWZEMPIC</div>
              <div className="footer-tagline">The Inference Diet</div>
            </div>
            <div className="footer-col">
              <div className="footer-col-title">Product</div>
              <ul className="footer-links">
                <li><a href="#pricing" onClick={(e) => { e.preventDefault(); handlePricingClick(); }}>Pricing</a></li>
                <li><a href="#how" onClick={(e) => { e.preventDefault(); document.getElementById('how')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}>How It Works</a></li>
                <li><a href="#">BYOO</a></li>
                <li><a href="https://docs.clawzempic.ai">Docs</a></li>
              </ul>
            </div>
            <div className="footer-col">
              <div className="footer-col-title">Company</div>
              <ul className="footer-links">
                <li><a href="#">About</a></li>
                <li><a href="#">Blog</a></li>
                <li><a href="#">Status</a></li>
              </ul>
            </div>
            <div className="footer-col">
              <div className="footer-col-title">Legal</div>
              <ul className="footer-links">
                <li><a href="#">Privacy Policy</a></li>
                <li><a href="#">Terms of Service</a></li>
                <li><a href="#">Security</a></li>
              </ul>
            </div>
          </div>
          <div className="footer-legal-bar">
            <span>&copy; 2026 Clawzempic. All rights reserved.</span>
            <div className="footer-legal-links">
              <a href="#">Privacy Policy</a>
              <a href="#">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
